import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * In-app update prompt for the desktop shell — replaces the native Windows
 * `dialog.showMessageBox` with a card in Bunker's own style.
 *
 * Two independent signals come from the Electron shell over the
 * `window.bunkerDesktop` bridge (absent in a normal browser, so this whole
 * component no-ops on the web):
 *
 *   1. content-update  — a new web deploy was detected while the app was open.
 *      Action: reload the window to pick up the freshly-served bundle.
 *   2. shell-update     — a new shell (.exe) version finished downloading.
 *      Action: quit and install it now.
 *
 * The shell only routes these to us when it advertises `styledPrompts: true`
 * (shipped alongside this component). Older shells keep their native dialog,
 * so there is never a double prompt during the rollout.
 */
export function DesktopUpdatePrompt() {
  const { t } = useTranslation();
  const [contentReady, setContentReady] = useState(false);
  const [shellVersion, setShellVersion] = useState<string | null>(null);

  useEffect(() => {
    const bridge = window.bunkerDesktop;
    if (!bridge?.isDesktop || !bridge.styledPrompts) return;
    bridge.onContentUpdate(() => setContentReady(true));
    bridge.onShellUpdateReady((v) => setShellVersion(v));
  }, []);

  // Shell update takes precedence — it's a bigger change and a reload would be
  // wasted if the .exe is about to be swapped anyway.
  if (shellVersion) {
    return (
      <UpdateCard
        title={t('ui.update.shellTitle')}
        detail={t('ui.update.shellDetail', { version: shellVersion })}
        confirmLabel={t('ui.update.shellConfirm')}
        onConfirm={() => window.bunkerDesktop?.installUpdate()}
        onDismiss={() => setShellVersion(null)}
      />
    );
  }
  if (contentReady) {
    return (
      <UpdateCard
        title={t('ui.update.contentTitle')}
        detail={t('ui.update.contentDetail')}
        confirmLabel={t('ui.update.contentConfirm')}
        onConfirm={() => window.bunkerDesktop?.reload()}
        onDismiss={() => setContentReady(false)}
      />
    );
  }
  return null;
}

function UpdateCard(props: {
  title: string;
  detail: string;
  confirmLabel: string;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  // The card sits in <body> as React Aria's "top layer": an open modal does
  // not make it inert, does not pull focus back out of it, and does not treat
  // a click on it as a click outside the dialog. Without this the card stays
  // visible over settings but its buttons cannot be pressed.
  return createPortal(
    <div
      data-react-aria-top-layer="true"
      className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-[1000] w-[340px] max-w-[calc(100vw-2rem)]"
      role="status"
      aria-live="polite"
    >
      <div className="bg-surface-secondary flex flex-col gap-3 rounded-2xl p-4 shadow-overlay">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent/15 text-accent"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v10m0 0 4-4m-4 4-4-4M5 19h14" />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-ink">{props.title}</div>
            <p className="mt-1 text-[13px] leading-snug text-ink-muted">{props.detail}</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={props.onDismiss}
            className="min-h-9 rounded-full px-3 py-2 text-[13px] font-medium text-ink-dim transition-colors hover:bg-white/5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {t('ui.update.later')}
          </button>
          <button
            type="button"
            onClick={props.onConfirm}
            className="min-h-9 rounded-full bg-accent px-4 py-2 text-[13px] font-semibold transition-colors hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            style={{ color: 'var(--accent-foreground)' }}
          >
            {props.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
